/**
 * DayView — 1일 × 24시간 시간축 (WeekView 와 동일 모델, 1컬럼)
 *
 * Phase 1 은 WeekView 단일 컬럼 버전. 추후 풍부한 상세 표시 (참석자, 위치 라벨 등) 확장.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { CalendarEvent, EventChipStyle } from '../../../models/CalendarEvent';
import { cn } from '../../../lib/utils';
import ChipMarkerIcon from './ChipMarkerIcon';
import { useCalendarLabels } from '../useCalendarLabels';

interface Props {
  anchorDate: Date;
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent, e?: React.MouseEvent) => void;
  onCellClick?: (date: Date) => void;
  getStyle: (event: CalendarEvent) => EventChipStyle;
}

const HOUR_HEIGHT = 56;
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function isOnDay(event: CalendarEvent, day: Date): boolean {
  const start = new Date(event.start_at);
  const end = new Date(event.end_at);
  const d0 = new Date(day); d0.setHours(0, 0, 0, 0);
  const d1 = new Date(d0); d1.setDate(d0.getDate() + 1);
  return start < d1 && end > d0;
}

function clampToDay(event: CalendarEvent, day: Date): { startMin: number; endMin: number } | null {
  const start = new Date(event.start_at);
  const end = new Date(event.end_at);
  const d0 = new Date(day); d0.setHours(0, 0, 0, 0);
  const d1 = new Date(d0); d1.setDate(d0.getDate() + 1);
  if (start >= d1 || end <= d0) return null;
  const startMin = Math.max(0, (start.getTime() - d0.getTime()) / 60000);
  const endMin = Math.min(1440, (end.getTime() - d0.getTime()) / 60000);
  return { startMin, endMin };
}

const DayView: React.FC<Props> = ({ anchorDate, events, onEventClick, onCellClick, getStyle }) => {
  const labels = useCalendarLabels();
  const today = new Date();
  const isToday = sameDay(anchorDate, today);

  const allDayEvents = useMemo(
    () => events.filter((e) => e.is_all_day && isOnDay(e, anchorDate)),
    [events, anchorDate],
  );
  const timedEvents = useMemo(
    () => events.filter((e) => !e.is_all_day && isOnDay(e, anchorDate)),
    [events, anchorDate],
  );

  const [now, setNow] = useState<Date>(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = HOUR_HEIGHT * 8;
    }
  }, []);

  const handleCellClick = (hour: number) => {
    if (!onCellClick) return;
    const d = new Date(anchorDate);
    d.setHours(hour, 0, 0, 0);
    onCellClick(d);
  };

  const dayLabel = anchorDate.toLocaleDateString(i18n.locale || 'en', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
  });

  return (
    <div className="flex flex-col h-full">
      {/* 날짜 헤더 */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border shrink-0">
        <h2 className={cn('text-body-lg font-semibold', isToday ? 'text-primary' : 'text-foreground')}>
          {dayLabel}
        </h2>
        {isToday && <span className="text-meta text-rose-500">● <Trans>Today</Trans></span>}
      </div>

      {/* 종일 슬롯 */}
      {allDayEvents.length > 0 && (
        <div className="grid grid-cols-[80px_1fr] border-b border-border shrink-0 min-h-[28px]">
          <div className="flex items-center justify-end pr-3 text-meta text-muted-foreground"><Trans>All day</Trans></div>
          <div className="p-0.5 flex flex-col gap-0.5">
            {allDayEvents.map((event) => {
              const style = getStyle(event);
              const categoryLabel = labels.category(style.category);
              return (
                <button
                  key={event.id}
                  onClick={(e) => onEventClick?.(event, e)}
                  className={cn(
                    'flex items-center gap-1.5 text-body font-medium px-2 py-1 rounded-md text-left hover:opacity-80 min-w-0',
                    style.filledBg,
                    style.borderExtra && `border ${style.borderExtra}`,
                  )}
                  style={event.color ? { backgroundColor: event.color, color: '#fff' } : undefined}
                  title={`${categoryLabel} · ${event.title}`}
                >
                  <ChipMarkerIcon marker={style.marker} className="size-3.5" />
                  <span className="truncate">{event.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 시간축 그리드 */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0">
        <div className="grid grid-cols-[80px_1fr] relative" style={{ height: `${HOUR_HEIGHT * 24}px` }}>
          {/* 시간 라벨 컬럼 */}
          <div className="relative border-r border-border">
            {HOURS.map((h) => (
              <div
                key={h}
                className="absolute -translate-y-1/2 right-3 text-meta text-muted-foreground tabular-nums"
                style={{ top: `${h * HOUR_HEIGHT}px` }}
              >
                {h === 0 ? '' : `${String(h).padStart(2, '0')}:00`}
              </div>
            ))}
          </div>

          {/* 본 컬럼 */}
          <div className={cn('relative', isToday && 'bg-primary/[0.02]')}>
            {HOURS.map((h) => (
              <div
                key={h}
                onClick={() => handleCellClick(h)}
                className={cn('absolute left-0 right-0 border-t border-border/40 transition-colors', onCellClick && 'cursor-pointer hover:bg-muted/30')}
                style={{ top: `${h * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
              />
            ))}

            {/* 현재 시각 가로선 */}
            {isToday && (
              <div
                className="absolute left-0 right-0 z-10 pointer-events-none"
                style={{ top: `${(now.getHours() * 60 + now.getMinutes()) / 60 * HOUR_HEIGHT}px` }}
              >
                <div className="h-px bg-rose-500" />
                <div className="absolute -left-1.5 -top-1.5 size-3 rounded-full bg-rose-500" />
              </div>
            )}

            {/* 시간 지정 일정 막대 */}
            {timedEvents.map((event) => {
              const range = clampToDay(event, anchorDate);
              if (!range) return null;
              const style = getStyle(event);
              const categoryLabel = labels.category(style.category);
              const top = range.startMin / 60 * HOUR_HEIGHT;
              const height = Math.max(28, (range.endMin - range.startMin) / 60 * HOUR_HEIGHT - 2);
              const startTime = new Date(event.start_at).toLocaleTimeString(i18n.locale || 'en', {
                hour: '2-digit', minute: '2-digit', hour12: false,
              });
              const endTime = new Date(event.end_at).toLocaleTimeString(i18n.locale || 'en', {
                hour: '2-digit', minute: '2-digit', hour12: false,
              });
              return (
                <button
                  key={event.id}
                  onClick={(e) => onEventClick?.(event, e)}
                  className={cn(
                    'absolute left-1 right-2 rounded-md px-2 py-1 text-left overflow-hidden hover:opacity-90 shadow-sm',
                    style.filledBg,
                    style.borderExtra && `border ${style.borderExtra}`,
                  )}
                  style={{
                    top: `${top}px`,
                    height: `${height}px`,
                    ...(event.color ? { backgroundColor: event.color, color: '#fff' } : {}),
                  }}
                  title={`${categoryLabel} · ${event.title} (${startTime} - ${endTime})`}
                >
                  <div className="flex items-center gap-1.5 text-label font-semibold leading-tight min-w-0">
                    <ChipMarkerIcon marker={style.marker} className="size-3.5" />
                    <span className="truncate">{event.title}</span>
                  </div>
                  <div className="text-meta opacity-80 leading-tight">{startTime} – {endTime}</div>
                  {event.location && height > 50 && (
                    <div className="text-meta opacity-80 leading-tight truncate mt-0.5">📍 {event.location}</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

    </div>
  );
};

export default DayView;
